import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Sparkles, FlaskConical, ShieldCheck, ShieldAlert,
  Camera, X, AlertTriangle, Leaf, Clock, Droplets, HardHat,
} from "lucide-react";
import { toast } from "sonner";
import { TopBar, BottomNav } from "../components/Nav";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { isNative, pickPhoto } from "../lib/native";

export default function ChemicalCheckPage() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const scanId = params.get("scan");

  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState("");
  const [disease, setDisease] = useState("");
  const [scanContext, setScanContext] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [advice, setAdvice] = useState(null);
  const [resultId, setResultId] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  // Pre-fill from a linked scan
  useEffect(() => {
    if (!scanId) return;
    axios.get(`${API}/scans/${scanId}`)
      .then((r) => {
        const res = r.data?.result || {};
        setScanContext({ crop: res.crop_name, disease: res.disease_name, image: r.data.thumbnail });
        if (res.crop_name) setCrop(res.crop_name);
        if (res.disease_name) setDisease(res.disease_name);
      })
      .catch(() => {});
  }, [scanId]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      toast.error(t("chem.tooLarge"));
      return;
    }
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(f);
  };

  const handleNativePick = async (source) => {
    try {
      const dataUrl = await pickPhoto({ source });
      if (dataUrl) setImage(dataUrl);
    } catch (e) {
      if (e?.message && !/cancel/i.test(e.message)) toast.error(e.message);
    }
  };
  const onCameraClick = () => (isNative() ? handleNativePick("camera") : camRef.current?.click());
  const onLibraryClick = () => (isNative() ? handleNativePick("library") : fileRef.current?.click());

  const verify = async () => {
    if (!image) {
      toast.error(t("chem.uploadFirst"));
      return;
    }
    setVerifying(true);
    setAdvice(null);
    try {
      const r = await axios.post(`${API}/verify-chemical`, {
        image_base64: image,
        language: lang,
        scan_id: scanId || undefined,
        crop_name: crop || undefined,
        disease_name: disease || undefined,
      });
      setAdvice(r.data.advice);
      setResultId(r.data.id);
      toast.success(t("chem.done"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("chem.failed"));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <Link to={scanId ? `/scan/${scanId}` : "/dashboard"} data-testid="chem-back" className="inline-flex items-center gap-1 text-[#1B4332] font-semibold hover:underline">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </Link>

        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#1B4332] flex items-center justify-center shrink-0">
            <FlaskConical className="w-5 h-5 text-[#D4A373]" />
          </div>
          <div>
            <h1 className="cd-heading text-3xl font-extrabold leading-tight">{t("chem.title")}</h1>
            <p className="text-sm text-[#52796F] mt-1">{t("chem.subtitle")}</p>
          </div>
        </div>

        {scanContext && (
          <div className="bg-[#F4F1EA] rounded-2xl p-4 border border-[#E5E0D8] flex items-center gap-3" data-testid="chem-scan-context">
            {scanContext.image && (
              <img src={scanContext.image} alt="scan" className="w-14 h-14 rounded-lg object-cover" />
            )}
            <div className="text-sm">
              <div className="font-bold text-[#1B4332]">{t("chem.linkedScan")}</div>
              <div className="text-[#2D3A35]">
                {scanContext.crop || "—"} · <span className="text-[#D90429]">{scanContext.disease || t("diag.unknown")}</span>
              </div>
            </div>
          </div>
        )}

        {/* Image picker */}
        <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 space-y-4">
          {image ? (
            <div className="relative">
              <img src={image} alt="chemical label" className="w-full max-h-72 object-contain rounded-xl bg-[#F4F1EA]" />
              <button
                data-testid="chem-clear-image"
                onClick={() => setImage(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 border border-[#E5E0D8]"
              >
                <X className="w-4 h-4 text-[#1C1C1C]" />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="chem-camera-btn"
                onClick={onCameraClick}
                className="flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed border-[#E5E0D8] hover:border-[#1B4332] hover:bg-[#F4F1EA] transition-colors"
              >
                <Camera className="w-6 h-6 text-[#1B4332]" />
                <span className="text-sm font-semibold">{t("chem.useCamera")}</span>
              </button>
              <button
                data-testid="chem-upload-btn"
                onClick={onLibraryClick}
                className="flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed border-[#E5E0D8] hover:border-[#1B4332] hover:bg-[#F4F1EA] transition-colors"
              >
                <Sparkles className="w-6 h-6 text-[#D4A373]" />
                <span className="text-sm font-semibold">{t("chem.uploadPhoto")}</span>
              </button>
              <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onPick} className="hidden" />
              <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-[#1C1C1C]">{t("chem.crop")} {t("common.optional")}</label>
              <input
                data-testid="chem-crop-input"
                value={crop}
                onChange={(e) => setCrop(e.target.value)}
                placeholder={t("chem.crop.placeholder")}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1B4332]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1C1C1C]">{t("chem.disease")} {t("common.optional")}</label>
              <input
                data-testid="chem-disease-input"
                value={disease}
                onChange={(e) => setDisease(e.target.value)}
                placeholder={t("chem.disease.placeholder")}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1B4332]"
              />
            </div>
          </div>

          <button
            data-testid="chem-verify-btn"
            onClick={verify}
            disabled={!image || verifying}
            className="w-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] py-4 rounded-full font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verifying ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> {t("chem.verifying")}</>
            ) : (
              <><Sparkles className="w-5 h-5" /> {t("chem.verify")}</>
            )}
          </button>
        </div>

        {advice && <AdviceCard advice={advice} t={t} resultId={resultId} />}
      </main>
      <BottomNav />
    </div>
  );
}

const AdviceCard = ({ advice, t }) => {
  const verdictTone = advice.is_appropriate === true
    ? { bg: "bg-[#2B9348]/10", border: "border-[#2B9348]/30", text: "text-[#2B9348]", icon: <ShieldCheck className="w-5 h-5" />, label: t("chem.verdict.safe") }
    : advice.is_appropriate === false
      ? { bg: "bg-[#D90429]/10", border: "border-[#D90429]/30", text: "text-[#D90429]", icon: <ShieldAlert className="w-5 h-5" />, label: t("chem.verdict.unsafe") }
      : { bg: "bg-[#E9C46A]/20", border: "border-[#E9C46A]/40", text: "text-[#8B5A00]", icon: <AlertTriangle className="w-5 h-5" />, label: t("chem.verdict.unclear") };

  if (!advice.is_label_readable) {
    return (
      <div data-testid="chem-result-unreadable" className="rounded-2xl p-5 border bg-[#E9C46A]/15 border-[#E9C46A]/40">
        <div className="flex items-center gap-2 font-bold text-[#8B5A00]">
          <AlertTriangle className="w-5 h-5" /> {t("chem.unreadable")}
        </div>
        <p className="text-sm text-[#2D3A35] mt-2">{advice.summary}</p>
      </div>
    );
  }

  return (
    <div data-testid="chem-result" className="space-y-4">
      <div className={`rounded-2xl p-5 border ${verdictTone.bg} ${verdictTone.border}`}>
        <div className={`flex items-center gap-2 font-bold text-lg ${verdictTone.text}`} data-testid="chem-verdict">
          {verdictTone.icon} {verdictTone.label}
        </div>
        {advice.product_name && (
          <div className="mt-3 text-sm text-[#1C1C1C]" data-testid="chem-product">
            <span className="font-bold">{advice.product_name}</span>
            {advice.active_ingredient && (
              <span className="text-[#52796F]"> · {advice.active_ingredient}</span>
            )}
          </div>
        )}
        {advice.appropriateness_reason && (
          <p className="text-sm text-[#2D3A35] mt-2">{advice.appropriateness_reason}</p>
        )}
        {advice.summary && (
          <p className="text-sm text-[#2D3A35] mt-2 italic">{advice.summary}</p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {advice.dosage && (
          <Detail icon={<Droplets className="w-4 h-4" />} label={t("chem.dosage")} value={advice.dosage} testid="chem-dosage" />
        )}
        {advice.spray_timing && (
          <Detail icon={<Clock className="w-4 h-4" />} label={t("chem.spray")} value={advice.spray_timing} testid="chem-spray" />
        )}
        {advice.pre_harvest_interval && (
          <Detail icon={<Leaf className="w-4 h-4" />} label={t("chem.phi")} value={advice.pre_harvest_interval} testid="chem-phi" />
        )}
        {advice.re_entry_interval && (
          <Detail icon={<Clock className="w-4 h-4" />} label={t("chem.rei")} value={advice.re_entry_interval} testid="chem-rei" />
        )}
      </div>

      {advice.ppe?.length > 0 && (
        <List icon={<HardHat className="w-4 h-4 text-[#1B4332]" />} title={t("chem.ppe")} items={advice.ppe} testid="chem-ppe" />
      )}
      {advice.warnings?.length > 0 && (
        <List icon={<AlertTriangle className="w-4 h-4 text-[#D90429]" />} title={t("chem.warnings")} items={advice.warnings} tone="danger" testid="chem-warnings" />
      )}
      {advice.organic_alternatives?.length > 0 && (
        <List icon={<Leaf className="w-4 h-4 text-[#2B9348]" />} title={t("chem.alternatives")} items={advice.organic_alternatives} testid="chem-alternatives" />
      )}
    </div>
  );
};

const Detail = ({ icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E5E0D8] p-4">
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#52796F] font-semibold">
      {icon} {label}
    </div>
    <div className="font-bold text-[#1C1C1C] mt-1.5">{value}</div>
  </div>
);

const List = ({ icon, title, items, tone, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl p-4 border ${tone === "danger" ? "bg-[#D90429]/5 border-[#D90429]/20" : "bg-white border-[#E5E0D8]"}`}
  >
    <div className="flex items-center gap-1.5 font-bold text-[#1C1C1C] mb-2">
      {icon} {title}
    </div>
    <ul className="text-sm text-[#2D3A35] space-y-1.5 list-disc pl-5">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  </div>
);
